import { useCallback, useMemo } from "react";
import { useSearchParamsState } from "shared/hooks/use-search-params-state";
import {
  deserializeArrayParam,
  deserializeObjectArrayParam,
  serializeArrayParam,
  serializeObjectArrayParam
} from "shared/utils/search-params-serialization";
import { PropertyKind } from "shared/types";
import { toPropertyKinds } from "shared/utils/property-kind-label";
import { PropertiesFilters } from "../types";

const URL_KEYS = [
  "property",
  "prop_states",
  "prop_kinds",
  "prop_screening",
  "prop_listing"
];

export const usePropertiesQueryParams = () => {
  const [rawParams, setRawParams] = useSearchParamsState(
    {
      property: "",
      prop_states: "",
      prop_kinds: "",
      prop_screening: "",
      prop_listing: ""
    },
    URL_KEYS
  );

  const filters = useMemo<PropertiesFilters>(() => {
    const states = deserializeArrayParam(rawParams.prop_states);
    const rawKinds = deserializeArrayParam(rawParams.prop_kinds);
    const validKinds = rawKinds ? toPropertyKinds(rawKinds) : undefined;
    const kinds = validKinds?.length ? validKinds : undefined;
    const screeningRaw = deserializeObjectArrayParam(rawParams.prop_screening);
    const listingRaw = deserializeObjectArrayParam(rawParams.prop_listing);

    return {
      ...(states ? { states } : {}),
      ...(kinds ? { propertyTypes: kinds } : {}),
      ...(screeningRaw ? { screeningTemplates: screeningRaw } : {}),
      ...(listingRaw ? { listingTemplates: listingRaw } : {})
    };
  }, [
    rawParams.prop_states,
    rawParams.prop_kinds,
    rawParams.prop_screening,
    rawParams.prop_listing
  ]);

  const search = rawParams.property ?? "";

  const setSearch = useCallback(
    (value: string) => {
      setRawParams({
        property: value,
        prop_states: rawParams.prop_states ?? "",
        prop_kinds: rawParams.prop_kinds ?? "",
        prop_screening: rawParams.prop_screening ?? "",
        prop_listing: rawParams.prop_listing ?? ""
      });
    },
    [rawParams, setRawParams]
  );

  const setFilters = useCallback(
    (next: PropertiesFilters) => {
      setRawParams({
        property: rawParams.property ?? "",
        prop_states: serializeArrayParam(next.states),
        prop_kinds: serializeArrayParam(next.propertyTypes as string[]),
        prop_screening: serializeObjectArrayParam(next.screeningTemplates),
        prop_listing: serializeObjectArrayParam(next.listingTemplates)
      });
    },
    [rawParams.property, setRawParams]
  );

  const removeState = useCallback(
    (value: string) => {
      const next = (filters.states ?? []).filter((s) => s !== value);
      setFilters({ ...filters, states: next.length ? next : undefined });
    },
    [filters, setFilters]
  );

  const removePropertyType = useCallback(
    (value: PropertyKind) => {
      const next = (filters.propertyTypes ?? []).filter((k) => k !== value);
      setFilters({
        ...filters,
        propertyTypes: next.length ? next : undefined
      });
    },
    [filters, setFilters]
  );

  const removeScreeningTemplate = useCallback(
    (id: string) => {
      const next = (filters.screeningTemplates ?? []).filter(
        (t) => t.id !== id
      );
      setFilters({
        ...filters,
        screeningTemplates: next.length ? next : undefined
      });
    },
    [filters, setFilters]
  );

  const removeListingTemplate = useCallback(
    (id: string) => {
      const next = (filters.listingTemplates ?? []).filter((t) => t.id !== id);
      setFilters({
        ...filters,
        listingTemplates: next.length ? next : undefined
      });
    },
    [filters, setFilters]
  );

  const activeFilterCount = useMemo(
    () =>
      (filters.states?.length ?? 0) +
      (filters.propertyTypes?.length ?? 0) +
      (filters.screeningTemplates?.length ?? 0) +
      (filters.listingTemplates?.length ?? 0),
    [filters]
  );

  return {
    search,
    filters,
    activeFilterCount,
    setSearch,
    setFilters,
    removeState,
    removePropertyType,
    removeScreeningTemplate,
    removeListingTemplate
  };
};
